import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { router } from 'expo-router';

import type {
  AttachedStatementReference,
  ExtractedStatement,
  StatementCitationStatus,
  StatementReferenceHelpRequest,
} from '../model/extractedStatements';
import { setCitationWizardHandoff } from '../state/citationWizardHandoffStore';
import { getProvideReferenceDraftStore } from '../state/provideReferenceDraftStore';
import { getStatementTriageStore } from '../state/statementTriageStore';

interface OpenProvideReferenceWizardInput {
  conversationId: string;
  /** Twilio SID of the parent expert message — the per-message store
   * registry key and the native route's `expectedMessageSid` guard. */
  messageSid: string;
  extractedStatements: readonly ExtractedStatement[];
  statementCitationStatuses: readonly StatementCitationStatus[] | undefined;
  attachedStatementReferences: readonly AttachedStatementReference[];
  statementReferenceHelpRequests?: readonly StatementReferenceHelpRequest[];
}

/**
 * Open the citation wizard in **provide** mode ("I have references in
 * mind") — the no-search sibling of `openCitationReviewWizard`. The two
 * are distinct bubble entries so the expert picks the flow up front,
 * where intent is clearest. Provide is a single step: it lands directly
 * on the `provide` phase with a row per extracted statement (each
 * opt-in), skipping selection — the expert already knows their
 * references and fills only the statements they have.
 *
 * - **Web**: resets the per-message triage store, flags
 *   `entryMode = 'provide'`, enters the provide phase seeded with all
 *   statement indices, and flips `isOpen`. The inline
 *   `StatementTriageSheet.web.tsx` reacts and renders the provide flow.
 * - **Native**: hands the inputs (with `entryMode: 'provide'`) to
 *   `citationWizardHandoffStore` and pushes the full-screen
 *   `citation-wizard` route, which applies the mode and enters the
 *   provide phase on mount.
 *
 * Always clears any stale provide drafts for the message first. Bails
 * (returns `false`) when there are no extracted statements — there is
 * nothing to bind a reference to. Returns `true` when a surface opened.
 */
export function openProvideReferenceWizard({
  conversationId,
  messageSid,
  extractedStatements,
  statementCitationStatuses,
  attachedStatementReferences,
  statementReferenceHelpRequests,
}: OpenProvideReferenceWizardInput): boolean {
  if (extractedStatements.length === 0) {
    return false;
  }

  // Start from a clean provide draft set so a prior session's values
  // never bleed into this one.
  getProvideReferenceDraftStore(messageSid).getState().reset();

  if (!isWeb) {
    setCitationWizardHandoff({
      conversationId,
      messageSid,
      extractedStatements: [...extractedStatements],
      statementCitationStatuses,
      attachedStatementReferences,
      statementReferenceHelpRequests,
      entryMode: 'provide',
    });

    router.push({
      pathname: '/(secure)/inbox/chat/citation-wizard',
      params: { expectedMessageSid: messageSid },
    });

    return true;
  }

  const store = getStatementTriageStore(messageSid);

  // reset() returns the wizard to a clean state (and clears any stale
  // search snapshot) before we flag provide mode — order matters because
  // reset() restores `entryMode` to its `search` default. enterProvide
  // jumps straight to the provide phase with a row per statement (no
  // selection step).
  store.getState().reset();

  store.getState().setEntryMode('provide');

  store.getState().enterProvide(extractedStatements.map((_statement, index) => index));

  store.getState().open();

  return true;
}
